import { Loading, SearchInput } from '@app/react-common';
import type { User } from '@bb/users';
import { Col, Flex, Row } from '@brokerbay/elements-web';
import { useQuery } from '@tanstack/react-query';
import { UserController } from 'api';
import {
  ANNOUNCEMENT_USER_TYPE_MAP,
  type AnnouncementUser,
} from 'api/AnnouncementController';
import { locations } from 'api/OrgController';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { Recipients } from '..';
import { RecipientsSearchTabs } from './RecipientsSearchTabs';
import RecipientsSelectionFilters from './RecipientsSelectionFilters';
import RecipientsTable from './RecipientsTable';

type AnnouncementManageRecipientsProps = {
  recipients: Recipients;
  authorizedLocations?: Set<string>;
  onUniqueCountChange: (count: number) => void;
  onRecipientsChange: (
    recipients: Recipients | ((prev: Recipients) => Recipients),
  ) => void;
};

const AnnouncementManageRecipients: React.FC<
  AnnouncementManageRecipientsProps
> = ({
  recipients,
  authorizedLocations,
  onUniqueCountChange,
  onRecipientsChange,
}) => {
  const [activeTab, setActiveTab] = useState<
    'locations' | 'teams' | 'groups' | 'users'
  >('locations');
  const [tableActiveTab, setTableActiveTab] = useState<
    'recipients' | 'omitted'
  >('recipients');

  const [uniqueUsersPage, setUniqueUsersPage] = useState(1);
  const uniqueUsersPerPage = 10;

  // keep a local debounced copy of recipients to avoid excessive refetches of uniqueUserData
  const [debouncedRecipients, setDebouncedRecipients] = useState(recipients);

  // Debounce recipients changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRecipients(recipients);
    }, 350);

    return () => clearTimeout(timer);
  }, [recipients]);

  const selectedTabMap = useMemo(() => {
    let result: Record<string, boolean> = {};

    switch (activeTab) {
      case 'locations':
        recipients.selectedLocations.forEach(l => {
          result[l.name] = true;
        });
        break;
      case 'teams':
      case 'groups':
        recipients.selectedGroups.forEach(g => {
          result[g.id] = true;
        });
        break;
      case 'users':
        recipients.selectedUsers.forEach(u => {
          result[u.id] = true;
        });
        break;
      default:
        result = {};
        break;
    }

    return result;
  }, [activeTab, recipients]);

  // Fetch locations
  const { data: locationsData, isLoading: isLoadingLocationData } = useQuery({
    queryKey: ['org-locations'],
    queryFn: locations,
  });

  // Fetch unique users based on debounced recipients
  const { data: uniqueUsersData, isFetching: isFetchingUniqueUsers } = useQuery(
    {
      queryKey: [
        'unique-users',
        debouncedRecipients,
        uniqueUsersPage,
        uniqueUsersPerPage,
      ],
      queryFn: async () => {
        const userIdsString = debouncedRecipients.selectedUsers
          .map(user => user.id)
          .join(',');
        const omittedIdsString = debouncedRecipients.omittedUsers
          .map(user => user.id)
          .join(',');
        const groupIdsString = debouncedRecipients.selectedGroups
          .map(group => group.id)
          .join(',');
        const locationIndexString = debouncedRecipients.selectedLocations
          .map(selectedLocation => selectedLocation.index)
          .join(',');
        const userTypeFilterString = debouncedRecipients.userTypeFilters
          .map(type => ANNOUNCEMENT_USER_TYPE_MAP[type])
          .join(',');

        const data = await UserController.getUniqueUsers({
          page: uniqueUsersPage - 1,
          limit: uniqueUsersPerPage,
          users: userIdsString,
          groups: groupIdsString,
          locations: locationIndexString,
          omits: omittedIdsString,
          typeFilters: userTypeFilterString,
        });

        return data;
      },
      onSuccess: data => {
        onUniqueCountChange(data?.count || 0);

        // Validate current page based on active tab
        const totalItems =
          tableActiveTab === 'recipients'
            ? data?.count || 0
            : recipients.omittedUsers.length;

        const maxPage = Math.max(1, Math.ceil(totalItems / uniqueUsersPerPage));

        if (uniqueUsersPage > maxPage) {
          setUniqueUsersPage(maxPage);
        }
      },
      keepPreviousData: true,
    },
  );

  const onItemSelect = (item: any) => (event: React.MouseEvent) => {
    event.preventDefault();

    const mapField = activeTab === 'locations' ? 'name' : 'id';

    // Check for duplicates using the derived map
    if (selectedTabMap[item[mapField]]) return;

    // Update recipients based on active tab
    onRecipientsChange(prevRecipients => {
      let updatedRecipients: Recipients;

      switch (activeTab) {
        case 'locations':
          updatedRecipients = {
            ...prevRecipients,
            selectedLocations: [...prevRecipients.selectedLocations, item],
          };
          break;
        case 'teams':
        case 'groups':
          updatedRecipients = {
            ...prevRecipients,
            selectedGroups: [...prevRecipients.selectedGroups, item],
          };
          break;
        case 'users':
          updatedRecipients = {
            ...prevRecipients,
            selectedUsers: [...prevRecipients.selectedUsers, item],
          };
          break;
        default:
          return prevRecipients;
      }

      // Focus search input (legacy behavior)
      document.getElementById('search-input-field')?.focus();

      return updatedRecipients;
    });
  };

  const onAddUserTypeFilter = (userType: AnnouncementUser) => {
    onRecipientsChange(prevRecipients => {
      if (prevRecipients.userTypeFilters.includes(userType)) {
        return prevRecipients;
      }

      const updatedRecipients: Recipients = {
        ...prevRecipients,
        userTypeFilters: [...prevRecipients.userTypeFilters, userType],
      };

      return updatedRecipients;
    });
  };

  const onRemoveFilter = (
    filterType: 'group' | 'location' | 'userType',
    id: number | string | AnnouncementUser,
  ) => {
    onRecipientsChange(prevRecipients => {
      let updatedRecipients: Recipients;

      switch (filterType) {
        case 'group':
          updatedRecipients = {
            ...prevRecipients,
            selectedGroups: prevRecipients.selectedGroups.filter(
              group => group.id !== id,
            ),
          };
          break;
        case 'location':
          updatedRecipients = {
            ...prevRecipients,
            selectedLocations: prevRecipients.selectedLocations.filter(
              location => location.index !== id,
            ),
          };
          break;
        case 'userType':
          updatedRecipients = {
            ...prevRecipients,
            userTypeFilters: prevRecipients.userTypeFilters.filter(
              type => type !== id,
            ),
          };
          break;
        default:
          return prevRecipients;
      }

      return updatedRecipients;
    });
  };

  const handleOmitUser = useCallback(
    (user: User) => {
      onRecipientsChange(prevRecipients => {
        // Check for duplicates using the functional update form
        if (prevRecipients.omittedUsers.findIndex(u => u.id === user.id) > -1) {
          return prevRecipients; // Return same reference if no change
        }
        return {
          ...prevRecipients,
          omittedUsers: [...prevRecipients.omittedUsers, user],
        };
      });
    },
    [onRecipientsChange],
  );

  const handleReinstateUser = useCallback(
    (user: User) => {
      onRecipientsChange(prevRecipients => ({
        ...prevRecipients,
        omittedUsers: prevRecipients.omittedUsers.filter(
          omittedUser => omittedUser.id !== user.id,
        ),
      }));
    },
    [onRecipientsChange],
  );

  if (isLoadingLocationData) {
    return <Loading size="fa-3x" />;
  }

  const uniqueCount = uniqueUsersData?.count || 0;

  return (
    <Row gutter={{ xs: 8, md: 16, xl: 32 }} justify="center">
      <Col xs={{ span: 24 }} lg={{ span: 10 }} xl={{ span: 8 }}>
        <StyledRecipientsSearchContainer>
          <RecipientsSearchTabs
            value={activeTab}
            onChange={e => {
              const newValue = e.target.value;
              setActiveTab(newValue);
            }}
          />
          <SearchInput
            locations={locationsData?.locations}
            authorizedLocations={authorizedLocations}
            type={activeTab}
            selectedIds={selectedTabMap}
            onItemSelect={onItemSelect}
          />
        </StyledRecipientsSearchContainer>
      </Col>
      <Col
        xs={{ span: 24 }}
        lg={{ span: 14 }}
        xl={{ span: 16 }}
        xxl={{ span: 14 }}
      >
        <StyledRecipientsFilterContainer>
          <RecipientsSelectionFilters
            uniqueCount={uniqueCount}
            selectedGroups={recipients.selectedGroups}
            selectedLocations={recipients.selectedLocations}
            userTypeFilters={recipients.userTypeFilters}
            onAddUserTypeFilter={onAddUserTypeFilter}
            onRemoveFilter={onRemoveFilter}
          />
          <RecipientsTable
            uniqueUserData={uniqueUsersData?.users || []}
            ommittedUserData={recipients.omittedUsers || []}
            locationIndex={locationsData?.locationIndex || {}}
            pageSize={uniqueUsersPerPage}
            totalUniqueUsers={uniqueCount}
            currentPage={uniqueUsersPage}
            activeTab={tableActiveTab}
            onActiveTabChange={setTableActiveTab}
            onPageChange={setUniqueUsersPage}
            onOmitUser={handleOmitUser}
            onReinstateUser={handleReinstateUser}
            isLoading={isFetchingUniqueUsers}
          />
        </StyledRecipientsFilterContainer>
      </Col>
    </Row>
  );
};

const StyledRecipientsSearchContainer = styled(Flex)`
  flex-direction: column;
  max-width: 500px;
  margin-bottom: 2rem;

  @media screen and (max-width: 992px) {
    max-width: 100%;
  }
`;

const StyledRecipientsFilterContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

export default AnnouncementManageRecipients;
